import { BaseApiService } from './BaseApiService';

export interface FileInfo {
  name: string;
  type: 'gfs' | 'ezcater' | 'ezcaterweekly' | 'toast' | 'toastweekly' | 'gfsquarterly' | 'ezcaterquarterly' | 'toastquarterly';
  path: string;
  size: number;
  lastModified: string;
  isActive: boolean;
  dateRange?: string | null;
}

export interface FileUploadResponse {
  success: boolean;
  message: string;
  fileInfo?: FileInfo;
}

export interface MultipleFileUploadResponse {
  success: boolean;
  message: string;
  uploaded: FileInfo[];
  errors: string[];
}

export interface FilesByType {
  gfs: FileInfo[];
  ezcater: FileInfo[];
  ezcaterweekly: FileInfo[];
  toast: FileInfo[];
  toastweekly: FileInfo[];
  gfsquarterly: FileInfo[];
  ezcaterquarterly: FileInfo[];
  toastquarterly: FileInfo[];
}

export class FileService extends BaseApiService {
  private static instance: FileService;

  private constructor() {
    super();
  }

  public static getInstance(): FileService {
    if (!FileService.instance) {
      FileService.instance = new FileService();
    }
    return FileService.instance;
  }

  async getAllFiles(): Promise<FilesByType> {
    return this.get<FilesByType>('/api/files', {}, false); // Don't cache file listings
  }

  async getFilesByType(fileType: FileInfo['type']): Promise<FileInfo[]> {
    // Use caching for file lists with smart invalidation
    return this.get<FileInfo[]>(`/api/files/type/${fileType}`, {}, true);
  }

  async uploadFile(file: File, fileType: 'gfs' | 'ezcater' | 'ezcaterweekly' | 'toast' | 'toastweekly' | 'gfsquarterly' | 'ezcaterquarterly' | 'toastquarterly'): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', fileType);

    return this.post<FileUploadResponse>('/api/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async uploadMultipleFiles(files: File[], fileType: 'gfs' | 'ezcater' | 'ezcaterweekly' | 'toast' | 'toastweekly' | 'gfsquarterly' | 'ezcaterquarterly' | 'toastquarterly'): Promise<MultipleFileUploadResponse> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('type', fileType);

    return this.post<MultipleFileUploadResponse>('/api/files/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async uploadConsolidatedFile(file: File): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.post<FileUploadResponse>('/api/files/upload/consolidated', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async getPreprocessedUploadUrl(fileName: string): Promise<{ uploadUrl: string; blobPath: string; fileName: string }> {
    const formData = new FormData();
    formData.append('fileName', fileName);
    return this.post<{ uploadUrl: string; blobPath: string; fileName: string }>('/api/files/upload/preprocessed/sas-url', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async uploadToBlobStorage(uploadUrl: string, file: File): Promise<void> {
    // Upload directly to Azure Blob Storage using PUT request
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': 'application/json',
      },
      body: file,
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Blob upload failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
  }

  async processPreprocessedDataFromBlob(blobPath: string): Promise<{ jobId: string; status: string; message: string }> {
    const formData = new FormData();
    formData.append('blobPath', blobPath);
    return this.post<{ jobId: string; status: string; message: string }>('/api/files/upload/preprocessed/process', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });
  }

  async uploadPreprocessedData(file: File): Promise<{ jobId: string; status: string; message: string }> {
    const FILE_SIZE_THRESHOLD = 20 * 1024 * 1024; // 20MB

    // Try blob upload first for large files
    if (file.size > FILE_SIZE_THRESHOLD) {
      try {
        const { uploadUrl, blobPath } = await this.getPreprocessedUploadUrl(file.name);
        await this.uploadToBlobStorage(uploadUrl, file);
        return await this.processPreprocessedDataFromBlob(blobPath);
      } catch (error: any) {
        console.warn('Blob upload failed, falling back to direct upload:', error);
      }
    }

    // Fallback to direct upload
    const formData = new FormData();
    formData.append('file', file);

    return this.post<{ jobId: string; status: string; message: string }>('/api/files/upload/preprocessed', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes for file upload (processing is async now)
    });
  }

  async getProcessingJobStatus(jobId: string): Promise<{
    jobId: string;
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
    progressPercent: number;
    statusMessage: string;
    fileName: string;
    locationsProcessed: number | null;
    expensesProcessed: number | null;
    errorMessage: string | null;
  }> {
    return this.get(`/api/files/preprocessed/job/${jobId}`, {}, false);
  }

  async exportAllFiles(): Promise<{ blob: Blob; filename: string }> {
    const response = await this.axiosInstance.get('/api/files/export/all', {
      responseType: 'blob',
      timeout: 600000, // 10 minutes for large file exports
    });
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `gfs_files_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.zip`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    return { blob: response.data, filename };
  }

  async deleteFile(fileName: string, fileType: 'gfs' | 'ezcater' | 'ezcaterweekly' | 'toast' | 'toastweekly' | 'gfsquarterly' | 'ezcaterquarterly' | 'toastquarterly'): Promise<{ success: boolean; message: string }> {
    // URL encode the fileName to handle special characters
    const encodedFileName = encodeURIComponent(fileName);
    return this.delete<{ success: boolean; message: string }>(`/api/files/${fileType}/${encodedFileName}`);
  }

  async getPreprocessedDataStatus(): Promise<{ hasData: boolean; locationCount: number; expenseCount: number; fileName?: string; uploadedAt?: string; preprocessedAt?: string }> {
    return this.get<{ hasData: boolean; locationCount: number; expenseCount: number; fileName?: string; uploadedAt?: string; preprocessedAt?: string }>('/api/files/preprocessed/status');
  }
}

export default FileService.getInstance();

